/**
 * Rating control module
 */
import { requestData } from "../utils";

const random_neg_margin = "random-negative-margin";

export interface FsDaysData {
  date: string;
  total: number;
  total_delivered: number;
  total_expired: number;
  total_rejected: number;
}

export interface EnterpriseFsApiResponse {
  enterprise_id: string;
  days: FsDaysData[];
}

export interface EnterpriseFsNetworkApiResponse {
  enterprise_id: string;
  days: Pick<FsDaysData, "date" | "total">[];
}

export interface FsGatewayApiResponse {
  gateway_id: string;
  days: Pick<FsDaysData, "date" | "total">[];
}

export interface FsNetworkGatewayApiResponse extends FsGatewayApiResponse {
  network_id: string;
}

export interface SubAccountQuery {
  'account-id': string;
  'from-date': string;
  'to-date': string;
}

export default class ReportingModule {
  sms: {
    mt: (params: any, random: boolean, abortSignal: AbortSignal) => Promise<any>;
    mt_network: (
      params: any,
      random: boolean,
      abortSignal: AbortSignal
    ) => Promise<any>;
    mt_account: (params: any, random: boolean) => Promise<any>;
    mt_subaccount: (params: SubAccountQuery) => Promise<any>;
    mt_enterprise: (
      params: any,
      random: boolean,
      abortSignal: AbortSignal
    ) => Promise<any>;
    mt_enterprise_network: (
      params: any,
      random: boolean,
      abortSignal: AbortSignal
    ) => Promise<any>;
    mt_account_network: (params: any, random: boolean) => Promise<any>;
    mt_billing_account: (params: any, random: boolean) => Promise<any>;
    mt_billing_account_network: (params: any, random: boolean) => Promise<any>;
    mt_gateway: (
      params: any,
      random: boolean,
      abortSignal: AbortSignal
    ) => Promise<any>;
    mt_gateway_network: (
      params: any,
      random: boolean,
      abortSignal: AbortSignal
    ) => Promise<any>;
    fs: (
      params: any,
      random: boolean,
      abortSignal: AbortSignal
    ) => Promise<Pick<EnterpriseFsNetworkApiResponse, "days">>;
    fs_network: (
      params: any,
      random: boolean,
      abortSignal: AbortSignal
    ) => Promise<Pick<FsNetworkGatewayApiResponse, "days" | "network_id">>;
    fs_account: (params: any, random: boolean) => Promise<any>;
    fs_enterprise: (
      params: any,
      random: boolean,
      abortSignal: AbortSignal
    ) => Promise<EnterpriseFsApiResponse>;
    fs_enterprise_network: (
      params: any,
      random: boolean,
      abortSignal: AbortSignal
    ) => Promise<EnterpriseFsNetworkApiResponse>;
    fs_account_network: (params: any, random: boolean) => Promise<any>;
    fs_reseller: (params: any, random: boolean) => Promise<any>;
    fs_billing_account: (params: any, random: boolean) => Promise<any>;
    fs_billing_account_network: (params: any, random: boolean) => Promise<any>;
    fs_gateway: (
      params: any,
      random: boolean,
      abortSignal: AbortSignal
    ) => Promise<FsGatewayApiResponse>;
    fs_gateway_network: (
      params: any,
      random: boolean,
      abortSignal: AbortSignal
    ) => Promise<FsNetworkGatewayApiResponse>;
    mo: (params: any, random: boolean) => Promise<any>;
    mo_account: (params: any, random: boolean) => Promise<any>;
    mo_gateway: (params: any, random: boolean) => Promise<any>;
    revenue: (params: any, random: boolean, abortSignal: AbortSignal) => Promise<any>;
    revenue_enterprise: (
      params: any,
      random: boolean,
      abortSignal: AbortSignal
    ) => Promise<any>;
  };
  mnp: {
    lookups: (params: any, random: boolean) => Promise<any>;
    lookups_handler: (
      params: any,
      random: boolean,
      abortSignal: AbortSignal
    ) => Promise<any>;
    lookups_handler_country: (params: any, random: boolean) => Promise<any>;
    lookups_country: (params: any, random: boolean) => Promise<any>;
  };
  confirmAPI: {
    twofa: (params: any, random: boolean, abortSignal: AbortSignal) => Promise<any>;
    twofa_enterprise: (params: any, random: boolean, abortSignal: AbortSignal) => Promise<any>;
  };
  constructor() {
    this.sms = {
      mt: (params, random = false, abortSignal) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData("GET", `/sms/mt`, params, undefined, abortSignal);
      },
      mt_network: (params, random = false, abortSignal) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData("GET", `/sms/mt/network`, params, undefined, abortSignal);
      },
      mt_account: (params, random = false) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData("GET", `/sms/mt/account`, params);
      },
      mt_subaccount: (params) => {
        return requestData("GET", `/sms/mt/account/subaccount`, params);
      },
      mt_enterprise: (params, random = false, abortSignal) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData(
          "GET",
          `/sms/mt/enterprise`,
          params,
          undefined,
          abortSignal
        );
      },
      mt_enterprise_network: (params, random = false, abortSignal) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData(
          "GET",
          `/sms/mt/enterprise/network`,
          params,
          undefined,
          abortSignal
        );
      },
      mt_account_network: (params, random = false) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData("GET", `/sms/mt/account/network`, params);
      },
      mt_billing_account: (params, random = false) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData("GET", `/sms/mt/billing_account`, params);
      },
      mt_billing_account_network: (params, random = false) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData("GET", `/sms/mt/billing_account/network`, params);
      },
      mt_gateway: (params, random = false, abortSignal) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData("GET", `/sms/mt/gateway`, params, undefined, abortSignal);
      },
      mt_gateway_network: (params, random = false, abortSignal) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData(
          "GET",
          `/sms/mt/gateway/network`,
          params,
          undefined,
          abortSignal
        );
      },
      fs: (params, random = false, abortSignal) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData("GET", `/sms/fs`, params, undefined, abortSignal);
      },
      fs_network: (params, random = false, abortSignal) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData("GET", `/sms/fs/network`, params, undefined, abortSignal);
      },
      fs_account: (params, random = false) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData("GET", `/sms/fs/account`, params);
      },
      fs_enterprise: (params, random = false, abortSignal) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData<EnterpriseFsApiResponse>(
          "GET",
          `/sms/fs/enterprise`,
          params,
          undefined,
          abortSignal
        );
      },
      fs_enterprise_network: (params, random = false, abortSignal) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData<EnterpriseFsNetworkApiResponse>(
          "GET",
          `/sms/fs/enterprise/network`,
          params,
          undefined,
          abortSignal
        );
      },
      fs_account_network: (params, random = false) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData("GET", `/sms/fs/account/network`, params);
      },
      fs_reseller: (params, random = false) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData("GET", `/sms/fs/reseller`, params);
      },
      fs_billing_account: (params, random = false) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData("GET", `/sms/fs/billing_account`, params);
      },
      fs_billing_account_network: (params, random = false) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData("GET", `/sms/fs/billing_account/network`, params);
      },
      fs_gateway: (params, random = false, abortSignal) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData("GET", `/sms/fs/gateway`, params, undefined, abortSignal);
      },
      fs_gateway_network: (params, random = false, abortSignal) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData(
          "GET",
          `/sms/fs/gateway/network`,
          params,
          undefined,
          abortSignal
        );
      },
      mo: (params, random = false) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData("GET", `/sms/mo`, params);
      },
      mo_account: (params, random = false) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData("GET", `/sms/mo/account`, params);
      },
      mo_gateway: (params, random = false) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData("GET", `/sms/mo/gateway`, params);
      },
      revenue: (params, random = false, abortSignal) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData("GET", `/sms/revenue`, params, undefined, abortSignal);
      },
      revenue_enterprise: (params, random = false, abortSignal) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData(
          "GET",
          `/sms/revenue/enterprise`,
          params,
          undefined,
          abortSignal
        );
      },
    };

    this.mnp = {
      lookups: (params, random = false) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData("GET", `/mnp/lookups`, params);
      },
      lookups_handler: (params, random = false, abortSignal) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData(
          "GET",
          `/mnp/lookups/handler`,
          params,
          undefined,
          abortSignal
        );
      },
      lookups_handler_country: (params, random = false) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData("GET", `/mnp/lookups/handler/country`, params);
      },
      lookups_country: (params, random = false) => {
        params.random = random;
        params[random_neg_margin] = random;
        return requestData("GET", `/mnp/lookups/country`, params);
      },
    };

    this.confirmAPI = {
      twofa: (params, random = false, abortSignal) => {
        params.random = random;
        return requestData("GET", `/confirm_api/twofa`, params, undefined, abortSignal);
      },
      twofa_enterprise: (params, random = false, abortSignal) => {
        params.random = random;
        return requestData("GET", `/confirm_api/twofa/enterprise`, params, undefined, abortSignal);
      }
    }
  }
}
